/**
 * React hook wrapper around `trackEvent`.
 *
 * Card n447gxvy — OBS F1.1 trackEvent helper + commonProps + entryChannel.
 *
 * Uso típico:
 *
 * ```tsx
 * const track = useTrackEvent({ userId: user?.sub, city: currentCity });
 * // ...
 * <button onClick={() => track('search_submit', { query })}>Buscar</button>
 * ```
 *
 * El caller pasa `defaultOptions` (contexto de usuario/página) una vez, y
 * las siguientes invocaciones adjuntan esos defaults sin repetirlos. La
 * memoización garantiza que el callback no cambie de identidad entre
 * renders con las mismas options — safe para pasar a `useEffect` deps.
 */
import { useCallback } from 'react';

import { trackEvent, type TrackEventOptions } from '../lib/analytics/trackEvent';

/**
 * Retorna un callback estable `track(name, props, overrideOptions?)`. Los
 * `defaultOptions` se aplican como base; el caller puede sobreescribirlos
 * en cada llamada individual.
 */
export function useTrackEvent(defaultOptions: TrackEventOptions = {}) {
  // Memoizamos por el shape completo — cambiar `userId` entre renders
  // debe re-generar el callback. `JSON.stringify` es simple y suficiente
  // para el shape plano de `TrackEventOptions`.
  const key = JSON.stringify(defaultOptions);

  return useCallback(
    (
      name: string,
      props: Record<string, unknown> = {},
      options: TrackEventOptions = {},
    ): Promise<void> => {
      return trackEvent(name, props, { ...defaultOptions, ...options });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key],
  );
}
